<template>
    <!-- Entscheid 13 (Auftrag 2026-08-16): Ist die Budget-Funktion
         ausgeblendet, ist hier nur noch das Inklusivbudget erfassbar — der
         Titel wechselt darum auf den bestehenden Schlüssel
         `topics.inclusiveBudget` (keine neue Zeile in den Sprachdateien). -->
    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title
            :title="showBudget ? $t('topics.editBudget') : $t('topics.inclusiveBudget')"
        ></page-title>
    </div>

    <jet-form-section @submitted="updateBudget()">
        <template #form>
            <!-- Card Budget -->
            <form-card
                v-if="showBudget"
                :title="$t('topics.budget')"
                :description="$t('topics.budgetDescription')"
                class="shadow-none"
            >
                <div class="col-span-3">
                    <!-- Netto-Umstellung 2026-08-18 (Spec-Entscheide 3 bis 5):
                         der Schalter sagt, wie der getippte Betrag gemeint
                         ist. Gespeichert wird immer netto.

                         SCHALTER STATT RADIO-KNOEPFEN: es ist derselbe
                         Umschalter mit denselben Beschriftungen wie in der
                         Abrechnungstabelle (`ApartmentBudgetTable.vue`) — der
                         Betreuer kennt ihn also schon, und die Maske bekommt
                         keine neue Bedienart. Zwei Radio-Knoepfe je Feld
                         waeren in einer Maske mit zwei Betragsfeldern vier
                         zusaetzliche Bedienelemente fuer dieselbe Aussage. -->
                    <div class="flex items-center justify-between">
                        <label
                            for="budget"
                            class="block text-sm font-medium text-gray-700"
                            >{{ $t("topics.budget") }}</label
                        >
                        <switch-input
                            data-testid="budget-vat-switch"
                            compact
                            :label="
                                form.budget_entered_gross
                                    ? $t('apartments.inclVat')
                                    : $t('apartments.exclVat')
                            "
                            :model-value="form.budget_entered_gross"
                            @update:modelValue="setBudgetGross"
                        />
                    </div>
                    <div class="flex">
                        <input
                            type="number"
                            name="number"
                            id="budget"
                            step="0.01"
                            v-model="form.budget"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <span
                            class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1"
                            >CHF</span
                        >
                    </div>
                    <jet-input-error :message="form.errors['budget']" class="mt-2" />
                </div>
            </form-card>

            <form-card
                :title="$t('topics.inclusiveBudget')"
                :description="$t('topics.inclusiveBudgetDescription')"
            >
                <div class="col-span-6">
                    <!-- Entscheid 5: dasselbe fuer das Inklusivbudget. Es geht
                         in keine Rechnung ein, steht aber im selben Modal —
                         zwei Betragsfelder mit unterschiedlicher
                         MWST-Bedeutung waeren die Sorte Detail, die in einem
                         Jahr niemand mehr weiss. -->
                    <div class="flex items-center justify-between">
                        <label
                            for="cost"
                            class="block text-sm font-medium text-gray-700"
                            >{{ $t("topics.inclusive_budget") }}</label
                        >
                        <switch-input
                            data-testid="inclusive-vat-switch"
                            compact
                            :label="
                                form.inclusive_budget_entered_gross
                                    ? $t('apartments.inclVat')
                                    : $t('apartments.exclVat')
                            "
                            :model-value="form.inclusive_budget_entered_gross"
                            @update:modelValue="setInclusiveGross"
                        />
                    </div>
                    <div class="flex">
                        <!-- `step` IST HIER PFLICHT, NICHT KOSMETIK
                             (UI-Gate der 5-Rappen-Rundung, 2026-08-26). Ein
                             `type="number"` OHNE `step` faellt auf den
                             HTML-Vorgabewert `1` zurueck, und der Browser
                             weist dann jeden Rappenbetrag ab: «Gib einen
                             gueltigen Wert ein. Die zwei naechstliegenden
                             gueltigen Werte sind 333 und 334.» Das
                             Inklusivbudget liess sich damit nur in ganzen
                             Franken erfassen — waehrend Entscheid 3.6 der
                             Rundungs-Spec es ausdruecklich als Betrag nennt,
                             der auf 5 Rappen gerundet wird. Das Budget-Feld
                             darueber traegt `step` seit jeher. -->
                        <input
                            type="number"
                            step="0.01"
                            name="inclusive_budget"
                            id="inclusive_budget"
                            v-model="form.inclusive_budget"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                        <span
                            class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1"
                            >CHF</span
                        >
                    </div>
                    <jet-input-error
                        :message="form.errors.inclusive_budget"
                        class="mt-2"
                    />
                </div>

                <hr class="col-span-6" />

                <div class="col-span-4">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{
                        $t("topics.inclusive_reference")
                    }}</label>
                    <!-- Dieselbe Falle bei der Referenzmenge — sie ist kein
                         Geldbetrag, aber eine Flaeche, und 85.55 m² ist der
                         Regelfall und nicht der Rand (dieselbe Begruendung
                         wie bei der Nettowohnflaeche im Anzeige-Paket). -->
                    <input
                        type="number"
                        step="0.01"
                        name="inclusive_reference"
                        id="inclusive_reference"
                        v-model="form.inclusive_reference"
                        class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                    />
                    <jet-input-error
                        :message="form.errors.inclusive_reference"
                        class="mt-2"
                    />
                </div>

                <div class="col-span-2">
                    <label
                        for="inclusive_unit"
                        class="block text-sm font-medium text-gray-700"
                        >{{ $t("topics.inclusive_unit") }}</label
                    >
                    <Dropdown
                        v-model="form.inclusive_unit"
                        :options="units"
                        optionLabel="name"
                        optionValue="value"
                        :showClear="false"
                        class="w-full mt-1 min-w-0 sm:text-sm"
                    >
                    </Dropdown>
                </div>
            </form-card>
        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">
                <button
                    @click="cancel"
                    type="button"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                >
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    class="ml-3"
                >
                    {{ $t("actions.save") }}
                </primary-button>
            </div>
        </template>
    </jet-form-section>
</template>

<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import FormCard from "@/Shared/Components/FormCard";
import PageTitle from "@/Shared/Components/PageTitle";
import SwitchInput from "@/Shared/Components/SwitchInput";
import { useForm } from "@inertiajs/inertia-vue3";
import { units } from "@/Pages/Advisor/Option/values";

export default defineComponent({
    // `vatFactor` kommt seit dem 2026-08-18 aus `TopicController::editBudget`
    // (Spec-Entscheide 3 bis 5). Fehlt er, ist er 1.0 — dieselbe
    // Nulltoleranz wie serverseitig in `VatRate`, und der Bestand ohne die
    // Prop verhaelt sich unveraendert.
    props: ["apartment", "topic", "feeCalculation", "vatFactor"],
    inheritAttrs: false,
    emits: ["closeModal"],
    layout: MainLayout,
    components: {
        PageTitle,
        FormCard,
        SwitchInput,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
    },
    computed: {
        // Fehlt die Prop, bleibt die Maske vollständig — der Bestand ohne
        // Modus-Angabe verhält sich unverändert.
        showBudget() {
            return this.feeCalculation ? this.feeCalculation.showBudget : true;
        },
        factor() {
            return Number(this.vatFactor) > 0 ? Number(this.vatFactor) : 1;
        },
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        /**
         * Umschalten rechnet den ANGEZEIGTEN Betrag um — also den, der
         * gerade im Feld steht, nicht den gespeicherten. Wer 10'810 exkl.
         * tippt und auf inkl. schaltet, sieht 11'685.61.
         */
        setBudgetGross(gross) {
            if (gross === this.form.budget_entered_gross) {
                return;
            }

            this.form.budget = this.convert(this.form.budget, gross);
            this.form.budget_entered_gross = gross;
        },
        setInclusiveGross(gross) {
            if (gross === this.form.inclusive_budget_entered_gross) {
                return;
            }

            this.form.inclusive_budget = this.convert(this.form.inclusive_budget, gross);
            this.form.inclusive_budget_entered_gross = gross;
        },
        convert(value, toGross) {
            const number = Number(value);

            if (!Number.isFinite(number)) {
                return value;
            }

            // Auf den Rappen, wie serverseitig (Entscheide 8 und 10).
            return (
                Math.round(
                    (toGross ? number * this.factor : number / this.factor) * 100
                ) / 100
            );
        },
        updateBudget() {
            this.form.put(
                this.route("advisors.topics.update", {
                    apartment: this.apartment.uuid,
                    topic: this.topic,
                }),
                {
                    preserveScroll: true,
                    onSuccess: () =>
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "topics.updated",
                        }),
                }
            );
        },
    },
    setup(props) {
        // Entscheid 13/16: Ist die Budget-Funktion ausgeblendet, enthält das
        // Formular gar kein `budget`-Feld. `UpdateTopicFeature` schreibt das
        // Budget nur bei `request()->has('budget')` — der gespeicherte Betrag
        // bleibt damit unangetastet. Der serverseitige Wächter im Feature ist
        // der Gürtel dazu.
        const showBudget = props.feeCalculation ? props.feeCalculation.showBudget : true;

        // Netto-Umstellung 2026-08-18 (Spec-Entscheide 3 bis 5): der Server
        // liefert den NETTO-Betrag, die gemerkte Schalterstellung und den
        // Faktor. Steht der Merker auf "inkl.", zeigt das Feld den
        // Bruttowert — der Betreuer findet die Zahl wieder, die er getippt
        // hat (Entscheid 4). Fehlt die Angabe, gilt "inkl." (Entscheid 3).
        const factor = Number(props.vatFactor) > 0 ? Number(props.vatFactor) : 1;
        const round = (value) => Math.round(value * 100) / 100;
        const display = (net, gross) =>
            gross ? round(Number(net || 0) * factor) : round(Number(net || 0));

        const budgetGross = props.topic.budget_entered_gross ?? true;
        const inclusiveGross = props.topic.inclusive_budget_entered_gross ?? true;

        const form = useForm({
            // Kein Betrag, kein Merker: `UpdateTopicFeature` liest die
            // Schalterstellung nur, wenn es das Budget auch schreibt.
            ...(showBudget
                ? {
                      budget: display(props.topic.budget, budgetGross),
                      budget_entered_gross: budgetGross,
                  }
                : {}),
            inclusive_budget: display(
                props.topic.inclusive_unformattedBudget,
                inclusiveGross
            ),
            inclusive_budget_entered_gross: inclusiveGross,
            inclusive_reference: props.topic.inclusive_reference,
            inclusive_unit: props.topic.inclusive_unit,
        });

        return { form, units };
    },
});
</script>

<style scoped></style>
